import { HTMLProps, useContext, useMemo, useState } from 'react';
import { RiskProfile } from '../../../api/robotapi';
import { RISK_PROFILE_FLUCTUATIONS } from '../../../config/risk-profiles';
import { ForecastContext, ForecastParameters } from '../../../contexts/investment-robot/ForecastContext';
import { toPrice } from '../../../utils/parsers';
import { Button } from '../../general/Button';
import { ForecastFundsChart } from './ForecastFundsChart';
import {
    CALL_EXPERT_URL,
    COLORS,
    DOCUMENT_CENTRUM_URL,
    ONBOARDING_LINKS,
    FAQ_INVESTMENT_ADVICE_URL,
} from '../../../config/constants';
import { roundFunds } from '../../../utils/functions';
import { Trans, useTranslation } from 'react-i18next';
import { GoalIndicationContext } from '../../../contexts/investment-robot/GoalIndicationContext';
import { useGetOnboardingLink } from '../../../api/investment-robot/useGetOnboardingLink';
import { useGetFutureCosts } from '../../../api/investment-robot/useGetFutureCosts';
import { RobotEnvironmentContext } from '../../../contexts/general/RobotEnvironmentContext';
import { useHistory } from 'react-router';
import { EmailMeModal } from './email-me-modal/EmailMeModal';
import { ForecastSummaryData } from './ForecastSummaryData';
import { IconElements } from '../../../config/icons';
import { Link } from '../../general/Link';

export const toPercentage = (x: number) => Math.round(x * 100);

const DocumentCentrumLinkText = () => {
    const { t } = useTranslation('investment');
    return (
        <Link className="block mt-8" href={DOCUMENT_CENTRUM_URL} target="_blank" rel="noreferrer" linkColor="green">
            {t('forecast-screen-calculation-see-document-centrum')}
        </Link>
    );
};

const InvestmentAdviceLinkText = () => {
    const { t } = useTranslation('investment');
    return (
        <p className="mt-8 ">
            <span>{t('forecast-screen-no-advice-disclaimer-text')}</span>
            <Link href={FAQ_INVESTMENT_ADVICE_URL} target="_blank" rel="noreferrer" linkColor="green">
                {t('forecast-screen-no-advice-disclaimer-link-text')}
            </Link>
        </p>
    );
};

export const SummaryEntry: React.FC<{ text: string; value: string } & HTMLProps<HTMLDivElement>> = ({
    text,
    value,
    className,
}) => {
    return (
        <div className={`flex flex-col lg:flex-row justify-between ${className}`}>
            <div className="flex flex-row items-center justify-between">
                <span className="block text-base">{text}</span>
            </div>
            <span className="block font-medium">{value}</span>
        </div>
    );
};

export const ForecastResultsCalculations: React.FC<HTMLProps<HTMLDivElement>> = ({ className }) => {
    const { forecast, riskProfiles } = useContext(ForecastContext);
    const futureCostsQuery = useGetFutureCosts(forecast.parameters);

    const [isEmailMeModalShown, setIsEmailMeModalShown] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const history = useHistory();

    const { t } = useTranslation('investment');

    const forecastParameters = forecast.parameters;
    const forecastData = forecast.query.data;

    const riskProfile = riskProfiles.query.data?.data.find(({ id }) => forecast.parameters.risk.profile.id === id + '');
    const riskFluctuation = RISK_PROFILE_FLUCTUATIONS.find((r) => r.id === riskProfile?.id + '');

    return (
        <div className="flex-1 flex-col text-lg">
            <div className={`flex lg:flex-row flex-col 2xl:rounded-lg text-bnd-black ${className}`} id="samenvatting">
                <div className="flex-1 p-4 md:p-8 lg:mr-10 bg-bnd-white-1 shadow-2xl rounded-2xl text-base">
                    <span className="block font-medium text-4xl">{t('forecast-screen-calculation-title')}</span>

                    <div className={forecastParameters.capitalGoal.amount > 0 ? '' : 'hidden'}>
                        <span className="block font-medium mt-8 text-xl">
                            {t('forecast-screen-calculation-configuration-title')}
                        </span>
                        <div>
                            <SummaryEntry
                                text={'In ' + forecast.parameters.capitalGoal.inYears + ' jaar:'}
                                value={toPrice(forecast.parameters.capitalGoal.amount, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                })}
                            />
                        </div>
                    </div>

                    <span className="block font-medium mt-8 text-xl">
                        {t('forecast-screen-calculation-goal-title')}
                    </span>
                    <div>
                        <SummaryEntry
                            text={t('forecast-screen-calculation-configuration-start-amount')}
                            value={toPrice(forecast.parameters.deposits.initial.amount, {
                                onlyFullEuros: true,
                                withCurrencySymbol: true,
                            })}
                        />
                        <SummaryEntry
                            text={t('forecast-screen-calculation-periodic-deposit', {
                                interval:
                                    forecast.parameters.deposits.periodic.interval === 'monthly' ? 'maand' : 'jaar',
                            })}
                            value={toPrice(forecast.parameters.deposits.periodic.amount, {
                                onlyFullEuros: true,
                                withCurrencySymbol: true,
                            })}
                        />
                        <SummaryEntry
                            text={t('forecast-screen-calculation-configuration-risk-profile')}
                            value={riskProfile?.description.toLowerCase() || 'geen'}
                        />
                        <SummaryEntry
                            text={t('forecast-screen-calculation-configuration-risk-reduction')}
                            value={forecast.parameters.risk.reduction.isEnabled ? 'ja' : 'geen'}
                        />
                    </div>
                    {riskFluctuation && (
                        <p className="mt-8">
                            <Trans
                                t={t}
                                i18nKey={'forecast-screen-calculation-configuration-risk-profile-description'}
                                values={{
                                    riskProfile: riskProfile?.description.toLowerCase(),
                                    optimisticFluctuation: riskFluctuation.optimistic,
                                    pessimisticFluctuation: riskFluctuation.pessimistic,
                                    suffix:
                                        riskProfile?.description === 'Zeer defensief'
                                            ? ''
                                            : t(
                                                  'forecast-screen-calculation-configuration-risk-profile-description-suffix',
                                              ),
                                }}
                            >
                                ...
                                <span className="font-medium">riskProfile</span>
                                <span className="font-medium">optimisticFluctuation</span>
                                <span className="font-medium">pessimisticFluctuation</span>
                                ...
                            </Trans>
                        </p>
                    )}

                    <ForecastSummaryData
                        className="mt-8"
                        classNameTitle="font-medium text-xl"
                        forecast={forecastData}
                        forecastParameters={forecast.parameters}
                        isLoading={forecast.query.isLoading}
                        isError={forecast.query.isError}
                        isFetching={forecast.query.isFetching}
                        isIdle={forecast.query.isIdle}
                    />
                    {futureCostsQuery.data?.total.netEndValue && (
                        <div className="mt-8">
                            <Trans
                                t={t}
                                i18nKey="forecast-screen-calculation-standard-return-rate-description"
                                values={{
                                    standardReturnRate: '4%',
                                    netEndValue: toPrice(futureCostsQuery.data.total.netEndValue, {
                                        onlyFullEuros: true,
                                        withCurrencySymbol: true,
                                    }),
                                }}
                            >
                                ...
                                <span className="font-medium">standardReturnRate</span>
                                <span className="font-medium">netEndValue</span>
                                ...
                            </Trans>
                        </div>
                    )}
                    <Button
                        buttonType="secondary"
                        className="mt-8 w-full"
                        onClick={() => {
                            history.push({
                                pathname: '/forecast',
                                state: {
                                    skipGoalIndication: true,
                                },
                            });
                        }}
                    >
                        {t('forceast-screen-calculation-back-to-result-button')}
                    </Button>
                    <DocumentCentrumLinkText />
                    <InvestmentAdviceLinkText />
                </div>

                <div style={{ flex: '3 2' }}>
                    {riskProfile && <FundsDivisionSection riskProfile={riskProfile} />}
                    <EmailMeModal
                        isShown={isEmailMeModalShown}
                        onClose={() => setIsEmailMeModalShown(false)}
                        setIsEmailSent={setIsEmailSent}
                    />
                    <ContinueSection
                        forecastParameters={forecast.parameters}
                        onClickSaveCalculation={() => setIsEmailMeModalShown(true)}
                        isEmailSent={isEmailSent}
                        className="mt-10"
                    />
                </div>
            </div>
        </div>
    );
};

const FundsDivisionSection: React.FC<{ riskProfile: RiskProfile } & HTMLProps<HTMLDivElement>> = ({
    riskProfile,
    className,
}) => {
    const { t } = useTranslation('investment');

    const { bonds, stocks } = useMemo(() => {
        if (!riskProfile) return { bonds: [], stocks: [] };

        const sorted = riskProfile.funds.sort((a, b) => b.weight - a.weight);

        const bonds = roundFunds(sorted.filter((f) => f.category === 'Bond'));
        const stocks = roundFunds(sorted.filter((f) => f.category === 'Stock'));

        return { bonds, stocks };
    }, [riskProfile]);

    const { bondTotal, stockTotal } = useMemo(() => {
        if (!riskProfile) return { bondTotal: undefined, stockTotal: undefined };

        const sumStock = riskProfile.funds
            .filter((f) => f.category === 'Stock')
            .reduce((total, fund) => total + fund.weight, 0);

        const sumBond = riskProfile.funds
            .filter((f) => f.category === 'Bond')
            .reduce((total, fund) => total + fund.weight, 0);

        return { bondTotal: toPercentage(sumBond), stockTotal: toPercentage(sumStock) };
    }, [riskProfile]);

    return (
        <div
            className={`p-4 md:p-8 flex flex-col flex-1 bg-bnd-white-1 2xl:m-0 shadow-2xl rounded-2xl text-base ${className}`}
        >
            <div className="flex-1 text-2xl sm:text-4xl font-medium">
                {t('forecast-screen-calculation-funds-title')}
            </div>
            <div className="flex-1">
                {t('forecast-screen-calculation-funds-subtitle', {
                    riskProfile: riskProfile.description,
                })}
            </div>

            <div className="flex flex-col 2xl:flex-row mt-8">
                <div className="flex-1 flex flex-col items-center justify-center">
                    <div
                        className="relative grid grid-cols-1 grid-rows-1 min-w-0 w-full"
                        style={{ minHeight: '250px' }}
                    >
                        <ForecastFundsChart stocks={stocks} bonds={bonds} />
                        <div className="absolute inset-0 pointer-events-none flex items-center justify-center text-bnd-black font-medium text-lg">
                            <div>{riskProfile?.description || 'geen'}</div>
                        </div>
                    </div>
                </div>
                <div className="flex-2 mt-8 2xl:mt-0 grid xl:grid-cols-2 lg:grid-cols-1 md:grid-cols-2 grid-cols-1 gap-8">
                    <div>
                        <div className="grid grid-cols-6 gap-4">
                            <div className="col-span-5 flex flex-row font-medium text-lg">
                                <div className="flex items-center justify-end">
                                    <div className="opacity-0 rounded-full w-4 h-4"></div>
                                </div>
                                <span className="ml-4">Aandelen</span>
                            </div>
                            <div className="col-span-1 font-medium text-lg text-right">
                                {stockTotal?.toLocaleString('nl-NL', {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                })}
                                %
                            </div>
                        </div>
                        {stocks.map((stock, index) => (
                            <div key={stock.id} className="mt-4 grid grid-cols-6 gap-4">
                                <div className="col-span-5 flex items-center">
                                    <div
                                        className="rounded-full w-4 h-4"
                                        style={{ backgroundColor: COLORS.shares[index] }}
                                    ></div>
                                    <div className="ml-4 flex-1">{stock.name}</div>
                                </div>
                                <div className="col-span-1  text-right">
                                    {stock.weight.toLocaleString('nl-NL', {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 1,
                                    })}
                                    %
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="">
                        <div className="grid grid-cols-6 gap-4">
                            <div className="col-span-5 flex flex-row font-medium text-lg">
                                <div className="col-span-1 flex items-center justify-end">
                                    <div className="opacity-0 rounded-full w-4 h-4"></div>
                                </div>
                                <span className="ml-4">Obligaties</span>
                            </div>
                            <div className="col-span-1 font-medium text-lg  text-right">
                                {bondTotal?.toLocaleString('nl-NL', {
                                    minimumFractionDigits: 1,
                                    maximumFractionDigits: 1,
                                })}
                                %
                            </div>
                        </div>
                        {bonds.map((bond, index) => (
                            <div key={bond.id} className="mt-4 grid grid-cols-6 gap-4">
                                <div className="col-span-5 flex items-center">
                                    <div
                                        className="rounded-full w-4 h-4"
                                        style={{ backgroundColor: COLORS.obligates[index] }}
                                    ></div>
                                    <div className="ml-4 flex-1">{bond.name}</div>
                                </div>

                                <div className="col-span-1 text-right">
                                    {bond.weight.toLocaleString('nl-NL', {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 1,
                                    })}
                                    %
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Link href={DOCUMENT_CENTRUM_URL} className="mt-8" target="_blank" rel="noreferrer" linkColor="green">
                {t('forecast-screen-calculation-funds-division-document-centrum')}
            </Link>
        </div>
    );
};

const ContinueSection: React.FC<
    {
        forecastParameters: ForecastParameters;
        onClickSaveCalculation: () => void;
        isEmailSent: boolean;
    } & HTMLProps<HTMLDivElement>
> = ({ onClickSaveCalculation, forecastParameters, isEmailSent, className }) => {
    const robotEnvironmentContext = useContext(RobotEnvironmentContext);
    const { mutateAsync: getOnboardingLink } = useGetOnboardingLink(forecastParameters);
    const goalIndicationContext = useContext(GoalIndicationContext);
    const { t } = useTranslation('investment');

    return (
        <div className={`p-4 md:p-8 flex flex-col flex-1 bg-bnd-white-1 shadow-2xl rounded-2xl text-base ${className}`}>
            <span className="block font-medium text-4xl mb-4">Hoe nu verder</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 grid-rows-3 sm:grid-rows-1 gap-y-4 sm:gap-y-0 sm:gap-x-4 mt-4">
                {robotEnvironmentContext.environment !== 'Advisor' && (
                    <Button
                        className="truncate"
                        buttonType="secondary"
                        onClick={async () => {
                            if (goalIndicationContext.parameters.selectedGoal === 'children') {
                                window.location.href = ONBOARDING_LINKS.CHILDREN;
                            } else {
                                const url = await getOnboardingLink();
                                window.location.href = url;
                            }
                        }}
                    >
                        <span className="flex-1 text-center">
                            {t('forecast-screen-result-calculation-become-customer')}
                        </span>
                    </Button>
                )}

                {robotEnvironmentContext.environment !== 'Advisor' && (
                    <>
                        <Button
                            buttonType="secondary"
                            className="truncate px-8 2xl:hidden"
                            onClick={() => {
                                window.open(CALL_EXPERT_URL, '_blank');
                            }}
                        >
                            Plan een belafspraak
                        </Button>
                        <Button
                            buttonType="secondary"
                            className="truncate px-8 hidden 2xl:block"
                            onClick={() => {
                                window.open(CALL_EXPERT_URL, '_blank');
                            }}
                        >
                            Plan een belafspraak
                        </Button>
                    </>
                )}
                <Button buttonType="primary" className="w-full" onClick={onClickSaveCalculation}>
                    <div className="flex flex-row justify-center items-center">
                        <IconElements.Banner className="w-6 h-6 mr-2 " />
                        {t('forecast-screen-result-save-calculation')}
                    </div>
                </Button>
                <div className="sm:col-end-4">
                    {isEmailSent && (
                        <div className="flex flex-row justify-center items-center text-bnd-green">
                            {t('forecast-screen-result-email-sent')}
                        </div>
                    )}
                </div>
                <div className="hidden md:flex md:flex-col"></div>
            </div>
        </div>
    );
};
